import { useParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { CreditCard } from 'lucide-react';
import { useVCardByUuid } from '@/api/account';
import { VCardPreview } from './components/VCardPreview';

/**
 * Public VCard page — resolves a VCard by UUID and renders the branded card.
 * Route: /vcard/:uuid (public, outside AppLayout's ProtectedRoute).
 * The API call itself still goes through the proxy; the UUID is the only
 * identifier needed — no auth on the viewer's side is implied by the spec.
 */
export default function VCardPublicPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const { data: vcard, isLoading, isError } = useVCardByUuid(uuid);

  return (
    <Container size="md">
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--vn-space-6) var(--vn-space-4)',
        }}
      >
        {isLoading ? (
          <Card flat style={{ width: '100%', maxWidth: 448 }}>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Skeleton style={{ height: 200 }} />
              <Skeleton style={{ height: 24 }} />
              <Skeleton style={{ height: 16, maxWidth: 200 }} />
            </CardBody>
          </Card>
        ) : isError ? (
          <Alert tone="danger" title="VCard not found">
            This link may be invalid or the card has been removed.
          </Alert>
        ) : !vcard ? (
          <EmptyState
            icon={<CreditCard size={24} />}
            title="VCard not found"
            description="This link may be invalid or the card has been removed."
          />
        ) : (
          <VCardPreview vcard={vcard} />
        )}
      </div>
    </Container>
  );
}
